import { ref, computed, watch, onMounted } from 'vue'
import { useForm, useField } from 'vee-validate'
import * as yup from 'yup'
import { storeToRefs } from 'pinia'
import router from '@/router'

// Stores
import { useUserStore } from '@/store'
import useCompany from '@/store/company'
import useNotifications from '@/store/notification'

// Services & Composables
import { getRoles } from '../services/RoleService'
import { getModules } from '@/shared/services/ModuleService'
import {
  createUser,
  getUserById,
  resendTemporaryPassword,
  updateUser,
} from '../services/UserService'
import useCompanies from '@/shared/composables/useCompanies'
import useEmployees from '@/shared/composables/useEmployees'

// Types & Utils
import Role from '../models/Role'
import Employee from '../../employees/models/Employee'
import CreateUserInputDto from '../services/interfaces/CreateUserInputDto'
import UpdateUserInputDto from '../services/interfaces/UpdateUserInputDto'
import roleNames from '@/shared/utils/rolesName'

export function useUserForm(userId?: string) {
  // Stores
  const { user } = storeToRefs(useUserStore())
  const { company } = storeToRefs(useCompany())
  const notificationStore = useNotifications()

  // Component state
  const isEditMode = ref(!userId)
  const userStatus = ref('')
  const username = ref('')
  const enabled = ref(true)

  // Loading states
  const isLoading = ref(false)
  const saving = ref(false)

  // Form State & Validation with vee-validate
  const { handleSubmit, meta, resetForm } = useForm({
    validationSchema: yup.object({
      name: yup.string().required('El nombre del usuario es requerido.'),
      email: yup
        .string()
        .email('Ingresa un email valido')
        .required('El email es requerido'),
      selectedRole: yup.object().required('El rol del usuario es requerido'),
      selectedEmployee: yup.object().nullable(),
      selectedCompanies: yup.array(),
      selectedModules: yup.array(),
      isRRHH: yup.boolean(),
    }),
  })

  const { value: name, errorMessage: nameError } = useField<string>('name')
  const { value: email, errorMessage: emailError } = useField<string>('email')
  const { value: selectedRole, errorMessage: roleError } =
    useField<Role>('selectedRole')
  const { value: selectedEmployee } = useField<Employee | undefined>(
    'selectedEmployee'
  )
  const { value: selectedCompanies } = useField<string[]>('selectedCompanies')
  const { value: selectedModules } = useField<string[]>('selectedModules')
  const { value: isRRHH } = useField<boolean>('isRRHH')

  // Data for selects/autocomplete
  const roles = ref<any[]>([])
  const adminModules = ref<any[]>([])
  const userCompanies = ref<any[]>([])
  const approverId = ref<string>()
  const employees = ref<Employee[]>([])
  const filteredEmployees = ref<Employee[]>([])

  // Computed properties for conditional rendering
  const isCompanySelectionVisible = computed(
    () =>
      ['admin', 'supervisor', 'admin users & supervisors', 'RRHH'].includes(
        selectedRole.value?.name || ''
      ) ||
      (selectedRole.value?.name === 'employee' && isRRHH.value)
  )
  const isModuleSelectionVisible = computed(() =>
    ['admin'].includes(selectedRole.value?.name || '')
  )

  const isRRHHAccessible = computed(
    () => !['RRHH'].includes(selectedRole.value?.name || '')
  )

  const fetchUserData = async () => {
    if (!userId) return
    isLoading.value = true
    try {
      const response = await getUserById(userId)
      userStatus.value = response.userStatus
      username.value = response.username
      enabled.value = response.enabled
      isRRHH.value = response.isRRHH
      approverId.value = response.approverId
      name.value = response.name
      email.value = response.email
      selectedRole.value = {
        renamedLabel: roleNames.get(response.role.name) || response.role.name,
        ...response.role,
      }
      selectedCompanies.value = response.companies?.map((c) => c.id) ?? []
      selectedModules.value = response.modules?.map((m) => m.id) ?? []
      // Set company context
      const companyStore = useCompany()
      companyStore.setCompany({
        client: response.client,
        ...response.company,
      })

      await fetchEmployees()

      selectedEmployee.value = employees.value.find(
        (e) => e.id === response.employee?.id
      )
    } finally {
      isLoading.value = false
    }
  }

  // Data Fetching
  const refreshAllData = async () => {
    isLoading.value = true
    try {
      const [rolesRes, modulesRes, companiesRes] = await Promise.all([
        getRoles(),
        getModules({ onlyAdminModules: true }),
        useCompanies(),
      ])

      const currentUserRole = user.value?.roles.name || ''
      const isSuperAdmin = currentUserRole === 'superadmin'
      roles.value = rolesRes.data
        .filter((r) => isSuperAdmin || r.name !== 'superadmin')
        .map((e) => ({ renamedLabel: roleNames.get(e.name) || e.name, ...e }))

      adminModules.value = modulesRes.data
      userCompanies.value = companiesRes?.companies?.value.map((c: any) => ({
        ...c,
        formattedLabel: `${c.code}-${c.name}`,
      }))

      await fetchEmployees()
    } finally {
      isLoading.value = false
    }
  }

  const fetchEmployees = async () => {
    employees.value = company.value
      ? (
          await useEmployees({
            companyId: company.value.id,
            requireUserData: false,
          })
        ).filter((e) => e.leaveDate == null)
      : []
  }

  // Lifecycle and Watchers
  onMounted(async () => {
    await refreshAllData()
    if (userId) {
      await fetchUserData()
    }
  })

  watch(company, async (newCompany) => {
    if (!newCompany) {
      resetForm({
        values: {
          name: '',
          email: '',
          selectedEmployee: undefined,
        },
      })
    }
    await fetchEmployees()
  })

  watch(selectedEmployee, (newEmployee) => {
    if (newEmployee && !userId) {
      email.value = newEmployee.email
      name.value = newEmployee.name
    }
  })

  watch(selectedRole, (newRole) => {
    if (newRole?.name !== 'admin') {
      selectedModules.value = []
    }

    // If the new role is NOT one that can have companies, clear the selection.
    const canHaveCompanies =
      ['admin', 'supervisor', 'admin users & supervisors', 'RRHH'].includes(
        newRole?.name || ''
      ) ||
      (newRole?.name === 'employee' && isRRHH.value)

    if (!canHaveCompanies) {
      selectedCompanies.value = []
    }
  })

  // Methods
  const searchEmployees = (event: any) => {
    const query = event.query.trim().toLowerCase()
    filteredEmployees.value = !query
      ? [...employees.value]
      : employees.value.filter(
          (e) =>
            e.code?.toLowerCase().startsWith(query) ||
            e.name.toLowerCase().startsWith(query)
        )
  }

  const employeeLabel = (employee: Employee | undefined) => {
    return employee ? `${employee.code} - ${employee.name}` : ''
  }

  const save = handleSubmit(async (values) => {
    saving.value = true
    try {
      if (userId) {
        const dto: UpdateUserInputDto = {
          clientId: company.value?.client.id,
          companyId: company.value?.id,
          email: values.email,
          name: values.name,
          roleId: values.selectedRole.id,
          enabled: enabled.value,
          userStatus: userStatus.value,
          isRRHH: values.isRRHH,
          approverId: approverId.value,
          ...(values.selectedEmployee && {
            employeeId: values.selectedEmployee.id,
            payrollId: values.selectedEmployee.payroll.id,
          }),
          ...(values.selectedCompanies?.length && {
            companies: values.selectedCompanies,
          }),
          ...(values.selectedModules?.length && {
            modules: values.selectedModules,
          }),
        }
        await updateUser(dto, userId)
        notificationStore.addSuccesNotification({
          summary: '¡Usuario actualizado con exito!',
          detail: 'El usuario ha sido actualizado correctamente.',
          severity: 'success',
        })
      } else {
        const dto: CreateUserInputDto = {
          clientId: company.value?.client.id,
          companyId: company.value?.id,
          email: values.email,
          name: values.name,
          roleId: values.selectedRole.id,
          isRRHH: values.isRRHH,
          ...(values.selectedEmployee && {
            employeeId: values.selectedEmployee.id,
            payrollId: values.selectedEmployee.payroll.id,
          }),
          ...(values.selectedCompanies?.length && {
            companies: values.selectedCompanies,
          }),
          ...(values.selectedModules?.length && {
            modules: values.selectedModules,
          }),
        }

        await createUser(dto)
        notificationStore.addSuccesNotification({
          summary: '¡Usuario creado con exito!',
          detail: 'El usuario ha sido creado correctamente.',
          severity: 'success',
        })
      }
      router.push('/users')
    } finally {
      saving.value = false
    }
  })

  const onResendTemporaryPassword = async () => {
    const response = await resendTemporaryPassword(
      username.value || '',
      userStatus.value || ''
    )
    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `201`,
        severity: 'success',
        summary: `Contraseña temporal reenviada con exito.`,
      })

      router.push('/users')
    }
  }

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }

  return {
    // State & Form Values
    name,
    email,
    selectedRole,
    selectedEmployee,
    selectedCompanies,
    selectedModules,
    isRRHH,
    // Data for UI
    roles,
    adminModules,
    userCompanies,
    filteredEmployees,
    // Errors
    nameError,
    emailError,
    roleError,
    // vee-validate meta
    meta,
    // Computed
    isCompanySelectionVisible,
    isModuleSelectionVisible,
    isRRHHAccessible,
    // Methods
    save,
    searchEmployees,
    employeeLabel,
    router,
    // Edit mode
    isEditMode,
    toggleEditMode,
    userStatus,
    username,
    onResendTemporaryPassword,
    isLoading,
    saving,
  }
}
